import { PlusIcon, XIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  MAX_BEATS,
  SUGGESTED_BEATS_BY_VERTICAL,
  normalizeBeat,
  normalizeBeats,
} from '@/lib/discover/beats';
import {
  ITEM_META,
  VALUE_CHIP,
  VALUE_CHIP_ADD,
  VALUE_CHIP_EMPTY,
  VALUE_CHIP_INTERACTIVE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface BeatsEditorProps {
  beats: string[];
  onChange: (beats: string[]) => void;
  /** Drives which starter phrases are offered. Usually the first vertical. */
  suggestionKey?: string | null;
  /**
   * An explicit suggestion list, used verbatim instead of the
   * `SUGGESTED_BEATS_BY_VERTICAL` lookup. This is what lets a second caller
   * (Followed brands, in `FirstVisitModal`) reuse this exact chip-input
   * pattern for a different vocabulary without forking a second combobox —
   * the taxonomy is the only thing that changes.
   */
  suggestions?: string[];
  /** Compact form for the sidebar; the modal uses the full form. */
  compact?: boolean;
  placeholder?: string;
  compactPlaceholder?: string;
  /** Shown once nothing is picked and there are no suggestions to offer. */
  emptyHint?: string;
  /** Label before the suggestion chips. Defaults to "Try:". */
  suggestionsLabel?: string;
  autoFocus?: boolean;
  /**
   * Daily email digest of new stories on these subjects. Rendered only when
   * `onDigestChange` is given, so a surface that cannot persist it never shows
   * a switch that does nothing. OFF unless the workspace turned it on.
   */
  digestEnabled?: boolean;
  onDigestChange?: (enabled: boolean) => void;
}

/**
 * The beats editor — free-text subjects, typed by the user.
 *
 * WHY THIS IS NOT A PICKER. Every other Discover preference is a chosen value
 * from a fixed taxonomy, because those values must match
 * `discover_articles.category`. A beat is the opposite by design: "data
 * centers", "economic obsolescence", "ERCOT interconnection" are subjects a
 * practice covers and no taxonomy will ever enumerate them. So this is an input
 * with suggestions, not a grid of chips.
 *
 * Suggestions are OFFERED, never applied. Silently seeding somebody's feed with
 * a subject they didn't ask for is the same class of defect as the Look that got
 * activated on a live account with no UI to explain it.
 */
export function BeatsEditor({
  beats,
  onChange,
  suggestionKey,
  suggestions: suggestionsProp,
  compact = false,
  placeholder,
  compactPlaceholder,
  emptyHint,
  suggestionsLabel = 'Try:',
  autoFocus = false,
  digestEnabled = false,
  onDigestChange,
}: BeatsEditorProps): JSX.Element {
  const [draft, setDraft] = React.useState('');

  const suggestions = React.useMemo(() => {
    const pool =
      suggestionsProp ?? (suggestionKey ? (SUGGESTED_BEATS_BY_VERTICAL[suggestionKey] ?? []) : []);
    const taken = new Set(beats.map((b) => b.toLowerCase()));
    return pool.filter((s) => !taken.has(s.toLowerCase()));
  }, [suggestionsProp, suggestionKey, beats]);

  const atLimit = beats.length >= MAX_BEATS;

  const add = (raw: string) => {
    const beat = normalizeBeat(raw);
    if (!beat || atLimit) return;
    if (beats.some((b) => b.toLowerCase() === beat.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange(normalizeBeats([...beats, beat]));
    setDraft('');
  };

  const remove = (beat: string) => onChange(beats.filter((b) => b !== beat));

  return (
    <div className="space-y-2">
      {beats.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {beats.map((beat) => (
            <span key={beat} className={cn(VALUE_CHIP, 'pr-1')}>
              {beat}
              <button
                type="button"
                onClick={() => remove(beat)}
                aria-label={`Remove ${beat}`}
                className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <XIcon weight="regular" className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {!atLimit && (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter AND comma: people type lists with commas without thinking.
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            }
            if (e.key === 'Backspace' && !draft && beats.length > 0) {
              remove(beats[beats.length - 1]);
            }
          }}
          onBlur={() => add(draft)}
          /* Neutral by design. The per-vertical `suggestions` below already
             show relevant examples for whoever this account actually is; a
             hardcoded example here would show one industry's vocabulary to
             every other industry. */
          placeholder={
            compact
              ? (compactPlaceholder ?? 'Add a subject')
              : (placeholder ?? 'Type a subject and press Enter')
          }
          maxLength={60}
          autoFocus={autoFocus}
        />
      )}

      {atLimit && (
        <p className={ITEM_META}>{MAX_BEATS} subjects is the limit. Remove one to add another.</p>
      )}

      {suggestions.length > 0 && !atLimit && (
        <div className="flex flex-wrap items-center gap-1.5">
          {!compact && <span className={cn(ITEM_META, 'mr-0.5')}>{suggestionsLabel}</span>}
          {suggestions.slice(0, compact ? 3 : 8).map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => add(suggestion)}
              className={VALUE_CHIP_ADD}
            >
              <PlusIcon className="size-3" />
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {onDigestChange && beats.length > 0 && (
        <label className="flex items-center justify-between gap-3">
          <span className={ITEM_META}>
            Email me a daily digest of new stories on these subjects
          </span>
          <Switch
            checked={digestEnabled}
            onCheckedChange={onDigestChange}
            aria-label="Daily email digest"
          />
        </label>
      )}

      {beats.length === 0 && suggestions.length === 0 && (
        <p className={ITEM_META}>
          {emptyHint ??
            "Name the subjects you cover. We'll pull matching stories to the top of your feed."}
        </p>
      )}
    </div>
  );
}

/** Read-only chips for a surface that only displays the current beats. */
export function BeatChips({
  beats,
  onClick,
  max = 12,
  coverage,
}: {
  beats: string[];
  onClick?: () => void;
  max?: number;
  /** Articles found per subject. A subject at zero is drawn dashed and says so:
   *  an empty subject used to look exactly like a working one. */
  coverage?: Record<string, number>;
}): JSX.Element | null {
  if (beats.length === 0) return null;
  const empty = coverage ? beats.filter((beat) => (coverage[beat] ?? 0) === 0) : [];
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {beats.slice(0, max).map((beat) => {
          const found = coverage?.[beat];
          const isEmpty = coverage !== undefined && (found ?? 0) === 0;
          return (
            <button
              key={beat}
              type="button"
              onClick={onClick}
              title={
                coverage === undefined
                  ? undefined
                  : isEmpty
                    ? 'No stories found for this subject yet'
                    : `${found} ${found === 1 ? 'story' : 'stories'} found`
              }
              className={cn(VALUE_CHIP, VALUE_CHIP_INTERACTIVE, isEmpty && VALUE_CHIP_EMPTY)}
            >
              {beat}
            </button>
          );
        })}
        {beats.length > max && <span className={VALUE_CHIP}>+{beats.length - max}</span>}
      </div>
      {empty.length > 0 && (
        <p className={cn(ITEM_META, 'mt-2')}>
          {emptySubjectsNote(
            empty.length,
            empty.filter((beat) => !beats.slice(0, max).includes(beat)).length,
            beats.length - max,
          )}
        </p>
      )}
    </div>
  );
}

/** "dashed above" is only true of the chips on screen; the rest sit inside the
 *  "+N" overflow, and claiming all of them as dashed sent people looking for
 *  chips that were not drawn. */
function emptySubjectsNote(total: number, hidden: number, overflow: number): string {
  const subjects = total === 1 ? '1 subject has' : `${total} subjects have`;
  if (hidden === 0) return `${subjects} no stories yet — dashed above.`;
  if (hidden === total) return `${subjects} no stories yet, all inside +${overflow}.`;
  return `${subjects} no stories yet — ${total - hidden} dashed above, ${hidden} inside +${overflow}.`;
}
